import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import './PostpartumSection.css';

function AppointmentEditModal({
  isOpen,
  onClose,
  onSave,
  appointment = null,
  isModify = false
}) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    scheduledDate: '',
    timeframe: '',
    priority: 'MEDIUM'
  });

  useEffect(() => {
    if (isOpen && appointment) {
      setFormData({
        name: appointment.name || '',
        description: appointment.description || '',
        scheduledDate: appointment.scheduledDate || appointment.date || '',
        timeframe: appointment.timeframe || '',
        priority: appointment.priority || 'MEDIUM'
      });
    } else if (isOpen && !appointment) {
      // New custom appointment
      setFormData({
        name: '',
        description: '',
        scheduledDate: '',
        timeframe: '',
        priority: 'MEDIUM'
      });
    }
  }, [isOpen, appointment]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    
    const appointmentData = {
      ...formData,
      type: appointment?.type || 'CUSTOM',
      suggestionId: isModify ? appointment?.id : null,
      accepted: true,
      custom: !appointment || appointment.custom || false
    };
    
    onSave(appointmentData);
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="appointment-edit-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="appointment-edit-modal-content"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="appointment-edit-modal-header">
            <h2>
              {appointment ? (isModify ? 'Modify Appointment' : 'Edit Appointment') : 'Add Custom Appointment'}
            </h2>
            <button className="close-button" onClick={onClose}>
              <FaTimes />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="appointment-edit-form">
            <div className="form-group">
              <label htmlFor="name">Appointment Name *</label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="e.g., 6-Week Postpartum Visit"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="3"
                placeholder="What is this appointment for?"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="scheduledDate">Scheduled Date</label>
                <input
                  id="scheduledDate"
                  type="date"
                  value={formData.scheduledDate}
                  onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label htmlFor="timeframe">Timeframe</label>
                <input
                  id="timeframe"
                  type="text"
                  value={formData.timeframe}
                  onChange={(e) => setFormData({ ...formData, timeframe: e.target.value })}
                  placeholder="e.g., 6-12 weeks"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="priority">Priority</label>
              <select
                id="priority"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              >
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
            </div>

            <div className="form-actions">
              <button type="button" className="cancel-button" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="save-button">
                {appointment ? (isModify ? 'Save Changes' : 'Update') : 'Add Appointment'}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

export default AppointmentEditModal;
